import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { POST_TYPE_META, type PostType } from "@/hooks/use-post-generation";
import { AssetPicker } from "./AssetPicker";
import { PostTypePreview } from "./PostTypePreview";
import {
  LinkedInOrganizationKnob,
  TikTokPrivacyKnob,
  RedditSubredditKnob,
  RedditFlairKnob,
  YoutubeCategoryKnob,
  YoutubePlaylistKnob,
  FacebookPageKnob,
  GmbLocationKnob,
} from "./SmartKnobs";
import {
  InstagramAudioKnob,
  InstagramCommentsKnob,
  InstagramLocationKnob,
  InstagramPaidPartnershipKnob,
  InstagramSponsorsKnob,
  InstagramTrialKnob,
  TikTokMusicKnob,
} from "./ReachKnobs";
import { ArrowUp, ArrowDown, Trash2, Plus, Image as ImageIcon, Film, FileText } from "lucide-react";

/**
 * PostType-aware editor — single component that exposes the right set of
 * fields depending on which PostType the piece is. Reads spec metadata
 * inlined here (in lockstep with packages/post-types/src/registry.ts) so the
 * web bundle doesn't need to import the server-only package.
 */

interface PostTypeUiSpec {
  fields: Array<
    | "title"
    | "caption"
    | "description"
    | "hashtags"
    | "threadItems"
    | "knob:subreddit"
    | "knob:flairId"
    | "knob:url"
    | "knob:documentTitle"
    | "knob:organizationUrn"
    | "knob:pageId"
    | "knob:locationId"
    | "knob:callToAction"
    | "knob:firstComment"
    | "knob:replySettings"
    | "knob:visibility"
    | "knob:madeForKids"
    | "knob:categoryId"
    | "knob:playlistId"
    | "knob:contentPreviewConfirmed"
    | "knob:expressConsentGiven"
    | "knob:privacyLevel"
    | "knob:videoMadeWithAi"
    | "knob:shareToFeed"
    | "knob:audioConfiguration"
    | "knob:trialParams"
    | "knob:isPaidPartnership"
    | "knob:brandedContentSponsors"
    | "knob:commentsEnabled"
    | "knob:musicSoundInfo"
  >;
  captionMax?: number;
  titleMax?: number;
  descriptionMax?: number;
  threadItemMax?: number;
}

const SPECS: Record<PostType, PostTypeUiSpec> = {
  IG_FEED: {
    fields: ["caption", "hashtags", "knob:firstComment", "knob:isPaidPartnership", "knob:brandedContentSponsors", "knob:commentsEnabled", "knob:locationId"],
    captionMax: 2200,
  },
  FB_FEED: { fields: ["caption", "hashtags", "knob:pageId", "knob:firstComment"], captionMax: 63206 },
  LINKEDIN_TEXT_OR_IMAGE: {
    fields: ["caption", "hashtags", "knob:organizationUrn", "knob:firstComment"],
    captionMax: 3000,
  },
  X_TWEET: { fields: ["caption", "knob:replySettings"], captionMax: 280 },
  THREADS_POST: { fields: ["caption"], captionMax: 500 },
  REDDIT_LINK: {
    fields: ["title", "knob:subreddit", "knob:url", "knob:flairId"],
    titleMax: 300,
  },
  REDDIT_MEDIA: { fields: ["title", "knob:subreddit", "knob:flairId"], titleMax: 300 },
  GBP_UPDATE: { fields: ["caption", "knob:locationId", "knob:callToAction"], captionMax: 1500 },
  IG_CAROUSEL: {
    fields: ["caption", "hashtags", "knob:firstComment", "knob:isPaidPartnership", "knob:brandedContentSponsors", "knob:commentsEnabled", "knob:locationId"],
    captionMax: 2200,
  },
  LINKEDIN_DOC: {
    fields: ["title", "caption", "hashtags", "knob:organizationUrn"],
    titleMax: 200,
    captionMax: 3000,
  },
  X_THREAD: { fields: ["threadItems"], threadItemMax: 280 },
  THREADS_THREAD: { fields: ["threadItems"], threadItemMax: 500 },
  REDDIT_SELF: {
    fields: ["title", "caption", "knob:subreddit", "knob:flairId"],
    titleMax: 300,
    captionMax: 40000,
  },
  IG_REEL: {
    fields: [
      "caption",
      "hashtags",
      "knob:shareToFeed",
      "knob:audioConfiguration",
      "knob:trialParams",
      "knob:isPaidPartnership", "knob:brandedContentSponsors", "knob:commentsEnabled", "knob:locationId",
    ],
    captionMax: 2200,
  },
  IG_STORY: { fields: ["caption"], captionMax: 2200 },
  FB_REEL: { fields: ["caption", "hashtags", "title"], captionMax: 500, titleMax: 100 },
  FB_STORY: { fields: ["caption"], captionMax: 500 },
  TIKTOK_VIDEO: {
    fields: [
      "caption",
      "hashtags",
      "knob:privacyLevel",
      "knob:videoMadeWithAi",
      "knob:musicSoundInfo",
      "knob:contentPreviewConfirmed",
      "knob:expressConsentGiven",
    ],
    captionMax: 4000,
  },
  TIKTOK_PHOTO: {
    fields: [
      "caption",
      "hashtags",
      "description",
      "knob:musicSoundInfo",
      "knob:contentPreviewConfirmed",
      "knob:expressConsentGiven",
    ],
    captionMax: 4000,
    descriptionMax: 4000,
  },
  YOUTUBE_SHORT: {
    fields: [
      "title",
      "caption",
      "hashtags",
      "knob:visibility",
      "knob:madeForKids",
      "knob:categoryId",
      "knob:playlistId",
    ],
    titleMax: 100,
    captionMax: 1000,
  },
  YOUTUBE_LONG: {
    fields: [
      "title",
      "description",
      "hashtags",
      "knob:visibility",
      "knob:madeForKids",
      "knob:categoryId",
      "knob:playlistId",
    ],
    titleMax: 100,
    descriptionMax: 5000,
  },
};

export interface PieceDraft {
  caption: string;
  title: string | null;
  description: string | null;
  hashtags: string[];
  threadItems: Array<{ content: string; mediaUrls?: string[] }> | null;
  platformKnobs: Record<string, any>;
  mediaUrls: string[];
}

interface MediaCapability {
  kind: "image" | "video" | "either" | "document" | "none";
  minItems: number;
  maxItems: number;
  ordered: boolean;
}

const MEDIA_CAPABILITY: Record<PostType, MediaCapability> = {
  IG_FEED: { kind: "image", minItems: 1, maxItems: 1, ordered: false },
  FB_FEED: { kind: "either", minItems: 0, maxItems: 1, ordered: false },
  LINKEDIN_TEXT_OR_IMAGE: { kind: "either", minItems: 0, maxItems: 1, ordered: false },
  X_TWEET: { kind: "either", minItems: 0, maxItems: 4, ordered: false },
  THREADS_POST: { kind: "either", minItems: 0, maxItems: 4, ordered: false },
  REDDIT_LINK: { kind: "none", minItems: 0, maxItems: 0, ordered: false },
  REDDIT_MEDIA: { kind: "image", minItems: 1, maxItems: 1, ordered: false },
  GBP_UPDATE: { kind: "either", minItems: 0, maxItems: 1, ordered: false },
  IG_CAROUSEL: { kind: "image", minItems: 2, maxItems: 10, ordered: true },
  LINKEDIN_DOC: { kind: "document", minItems: 1, maxItems: 1, ordered: false },
  X_THREAD: { kind: "either", minItems: 0, maxItems: 4, ordered: false },
  THREADS_THREAD: { kind: "either", minItems: 0, maxItems: 4, ordered: false },
  REDDIT_SELF: { kind: "none", minItems: 0, maxItems: 0, ordered: false },
  IG_REEL: { kind: "video", minItems: 1, maxItems: 1, ordered: false },
  IG_STORY: { kind: "either", minItems: 1, maxItems: 1, ordered: false },
  FB_REEL: { kind: "video", minItems: 1, maxItems: 1, ordered: false },
  FB_STORY: { kind: "either", minItems: 1, maxItems: 1, ordered: false },
  TIKTOK_VIDEO: { kind: "video", minItems: 1, maxItems: 1, ordered: false },
  TIKTOK_PHOTO: { kind: "image", minItems: 1, maxItems: 35, ordered: true },
  YOUTUBE_SHORT: { kind: "video", minItems: 1, maxItems: 1, ordered: false },
  YOUTUBE_LONG: { kind: "video", minItems: 1, maxItems: 1, ordered: false },
};

function pickerTypeFor(kind: MediaCapability["kind"]): "IMAGE" | "VIDEO" | "DOCUMENT" | undefined {
  if (kind === "image") return "IMAGE";
  if (kind === "video") return "VIDEO";
  if (kind === "document") return "DOCUMENT";
  return undefined; // "either" → no filter
}

interface Props {
  brandId: string;
  postType: PostType;
  initial: PieceDraft;
  onSave: (changes: Partial<PieceDraft>) => Promise<void>;
  onRegenerateMedia?: () => Promise<void> | void;
  regenerateMediaPending?: boolean;
  brandHandle?: string | null;
  brandName?: string | null;
  /** When true, shows a side-by-side preview pane. Defaults to true. */
  showPreview?: boolean;
  disabled?: boolean;
}

function CharCounter({ value, max }: { value: number; max: number | undefined }) {
  if (!max) return null;
  const over = value > max;
  return (
    <span className={`text-[10px] ${over ? "text-rose-600" : "text-muted-foreground"}`}>
      {value}/{max}
    </span>
  );
}

export function PostTypeEditor({
  brandId,
  postType,
  initial,
  onSave,
  onRegenerateMedia,
  regenerateMediaPending,
  brandHandle,
  brandName,
  showPreview = true,
  disabled,
}: Props) {
  const { t } = useTranslation("compose");
  const spec = SPECS[postType];
  const meta = POST_TYPE_META[postType];
  const mediaCap = MEDIA_CAPABILITY[postType];

  const [draft, setDraft] = useState<PieceDraft>(initial);
  useEffect(() => setDraft(initial), [initial, postType]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial]);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [threadPickerIdx, setThreadPickerIdx] = useState<number | null>(null);

  const updateKnob = (key: string, value: any) =>
    setDraft((d) => ({ ...d, platformKnobs: { ...d.platformKnobs, [key]: value } }));

  const moveMedia = (i: number, dir: -1 | 1) => {
    const arr = [...draft.mediaUrls];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setDraft({ ...draft, mediaUrls: arr });
  };
  const removeMedia = (i: number) => {
    setDraft({ ...draft, mediaUrls: draft.mediaUrls.filter((_, j) => j !== i) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        caption: draft.caption,
        title: draft.title,
        description: draft.description,
        hashtags: draft.hashtags,
        threadItems: draft.threadItems,
        platformKnobs: draft.platformKnobs,
        mediaUrls: draft.mediaUrls,
      });
    } finally {
      setSaving(false);
    }
  };

  const editorBody = (
    <div className="space-y-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wider">
        {meta.platform} · {meta.label} (Tier {meta.tier})
      </div>

      {mediaCap.kind !== "none" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium uppercase text-muted-foreground">
              {t("editor.media")}{" "}
              <span className="normal-case text-[10px]">
                {mediaCap.kind === "either"
                  ? t("editor.mediaKind.either")
                  : mediaCap.kind === "document"
                    ? t("editor.mediaKind.document")
                    : `(${mediaCap.kind})`}
              </span>
            </label>
            <span className="text-[10px] text-muted-foreground">
              {draft.mediaUrls.length}/{mediaCap.maxItems}
              {mediaCap.minItems > 0 ? t("editor.mediaMin", { min: mediaCap.minItems }) : ""}
            </span>
          </div>
          {draft.mediaUrls.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {draft.mediaUrls.map((url, i) => {
                const isVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
                const isPdf = /\.pdf(\?|$)/i.test(url);
                const Icon = isVideo ? Film : isPdf ? FileText : ImageIcon;
                return (
                  <div
                    key={`${url}-${i}`}
                    className="relative w-20 h-20 rounded border overflow-hidden bg-muted group"
                  >
                    {isVideo || isPdf ? (
                      <div className="w-full h-full flex flex-col items-center justify-center text-[10px] text-muted-foreground">
                        <Icon className="h-6 w-6" />
                        {isVideo ? t("editor.video") : t("editor.pdf")}
                      </div>
                    ) : (
                      <img src={url} alt="" className="object-cover w-full h-full" />
                    )}
                    {mediaCap.ordered && (
                      <span className="absolute top-1 left-1 bg-primary text-primary-foreground rounded-full text-[10px] w-5 h-5 flex items-center justify-center font-medium">
                        {i + 1}
                      </span>
                    )}
                    <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity p-0.5">
                      {mediaCap.ordered && (
                        <div className="flex gap-0.5">
                          <button
                            type="button"
                            onClick={() => moveMedia(i, -1)}
                            disabled={i === 0 || disabled}
                            className="text-white/80 hover:text-white disabled:opacity-30"
                            aria-label={t("editor.moveUp")}
                          >
                            <ArrowUp className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveMedia(i, 1)}
                            disabled={i === draft.mediaUrls.length - 1 || disabled}
                            className="text-white/80 hover:text-white disabled:opacity-30"
                            aria-label={t("editor.moveDown")}
                          >
                            <ArrowDown className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeMedia(i)}
                        disabled={disabled}
                        className="text-rose-300 hover:text-rose-200 ml-auto"
                        aria-label={t("editor.remove")}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!pickerOpen ? (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled || draft.mediaUrls.length >= mediaCap.maxItems}
                onClick={() => setPickerOpen(true)}
                className="gap-1"
              >
                <Plus className="h-3.5 w-3.5" />
                {draft.mediaUrls.length === 0 ? t("editor.pickMedia") : t("editor.addMore")}
              </Button>
              {mediaCap.kind === "image" && draft.mediaUrls.length > 0 && onRegenerateMedia && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled || regenerateMediaPending}
                  onClick={() => onRegenerateMedia()}
                  className="text-xs"
                  title={t("editor.reOptimizeTitle")}
                >
                  {regenerateMediaPending ? t("editor.reOptimizing") : t("editor.reOptimizeMedia")}
                </Button>
              )}
            </div>
          ) : (
            <AssetPicker
              brandId={brandId}
              type={pickerTypeFor(mediaCap.kind)}
              selectedUrls={draft.mediaUrls}
              multi={mediaCap.maxItems > 1}
              max={mediaCap.maxItems}
              onChange={(urls) => setDraft({ ...draft, mediaUrls: urls })}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </div>
      )}

      {spec.fields.includes("title") && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium uppercase text-muted-foreground">{t("editor.title")}</label>
            <CharCounter value={(draft.title ?? "").length} max={spec.titleMax} />
          </div>
          <input
            type="text"
            value={draft.title ?? ""}
            disabled={disabled}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            maxLength={spec.titleMax ? spec.titleMax + 50 : undefined}
            className="block w-full rounded border px-3 py-2 text-sm"
          />
        </div>
      )}

      {spec.fields.includes("caption") && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium uppercase text-muted-foreground">{t("editor.caption")}</label>
            <CharCounter value={draft.caption.length} max={spec.captionMax} />
          </div>
          <textarea
            value={draft.caption}
            disabled={disabled}
            onChange={(e) => setDraft({ ...draft, caption: e.target.value })}
            rows={6}
            className="block w-full rounded border px-3 py-2 text-sm"
          />
        </div>
      )}

      {spec.fields.includes("description") && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium uppercase text-muted-foreground">
              {t("editor.description")}
            </label>
            <CharCounter value={(draft.description ?? "").length} max={spec.descriptionMax} />
          </div>
          <textarea
            value={draft.description ?? ""}
            disabled={disabled}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            rows={8}
            className="block w-full rounded border px-3 py-2 text-sm"
          />
        </div>
      )}

      {spec.fields.includes("hashtags") && (
        <div>
          <label className="text-xs font-medium uppercase text-muted-foreground">{t("editor.hashtags")}</label>
          <input
            type="text"
            value={draft.hashtags.join(" ")}
            disabled={disabled}
            onChange={(e) =>
              setDraft({
                ...draft,
                hashtags: e.target.value.split(/\s+/).map((s) => s.replace(/^#/, "")).filter(Boolean),
              })
            }
            placeholder={t("editor.hashtagsPlaceholder")}
            className="mt-1 block w-full rounded border px-3 py-2 text-sm font-mono"
          />
        </div>
      )}

      {spec.fields.includes("threadItems") && (
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase text-muted-foreground">
            {t("editor.threadItems")}
          </label>
          {(draft.threadItems ?? []).map((item, i) => {
            const itemMedia = item.mediaUrls ?? [];
            return (
              <div key={i} className="rounded border p-2 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase text-muted-foreground">#{i + 1}</span>
                  <div className="flex items-center gap-2">
                    <CharCounter value={item.content.length} max={spec.threadItemMax} />
                    <button
                      onClick={() =>
                        setDraft({
                          ...draft,
                          threadItems: (draft.threadItems ?? []).filter((_, j) => j !== i),
                        })
                      }
                      className="text-[10px] text-rose-600 hover:underline"
                      disabled={disabled}
                    >
                      {t("editor.removeItem")}
                    </button>
                  </div>
                </div>
                <textarea
                  value={item.content}
                  onChange={(e) => {
                    const items = [...(draft.threadItems ?? [])];
                    items[i] = { ...items[i], content: e.target.value };
                    setDraft({ ...draft, threadItems: items });
                  }}
                  rows={2}
                  disabled={disabled}
                  className="block w-full rounded border px-2 py-1 text-xs"
                />
                {/* Per-item media slot */}
                <div className="flex flex-wrap gap-1 items-center">
                  {itemMedia.map((url, m) => {
                    const isVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
                    return (
                      <div key={`${url}-${m}`} className="relative w-12 h-12 rounded border overflow-hidden bg-muted group">
                        {isVideo ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <Film className="h-4 w-4 text-muted-foreground" />
                          </div>
                        ) : (
                          <img src={url} alt="" className="object-cover w-full h-full" />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const items = [...(draft.threadItems ?? [])];
                            const next = (items[i].mediaUrls ?? []).filter((_, idx) => idx !== m);
                            items[i] = { ...items[i], mediaUrls: next };
                            setDraft({ ...draft, threadItems: items });
                          }}
                          disabled={disabled}
                          className="absolute inset-x-0 bottom-0 bg-black/60 text-rose-300 hover:text-rose-200 opacity-0 group-hover:opacity-100 transition-opacity flex justify-center"
                          aria-label={t("editor.removeMedia")}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setThreadPickerIdx(threadPickerIdx === i ? null : i)}
                    disabled={disabled || itemMedia.length >= 4}
                    className="rounded border-dashed border w-12 h-12 flex items-center justify-center text-xs text-muted-foreground hover:bg-muted disabled:opacity-50"
                    aria-label={t("editor.addMedia")}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                {threadPickerIdx === i && (
                  <AssetPicker
                    brandId={brandId}
                    selectedUrls={itemMedia}
                    multi={true}
                    max={4}
                    onChange={(urls) => {
                      const items = [...(draft.threadItems ?? [])];
                      items[i] = { ...items[i], mediaUrls: urls };
                      setDraft({ ...draft, threadItems: items });
                    }}
                    onClose={() => setThreadPickerIdx(null)}
                  />
                )}
              </div>
            );
          })}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() =>
              setDraft({
                ...draft,
                threadItems: [...(draft.threadItems ?? []), { content: "", mediaUrls: [] }],
              })
            }
          >
            {t("editor.addItem")}
          </Button>
        </div>
      )}

      {spec.fields
        .filter((f) => f.startsWith("knob:"))
        .map((f) => {
          const key = f.slice("knob:".length);
          const value = draft.platformKnobs[key] ?? "";

          // ── Smart-knob dispatch: API-backed pickers replace free-text. ──
          if (key === "organizationUrn") {
            return (
              <LinkedInOrganizationKnob
                key={key}
                brandId={brandId}
                value={value}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }
          if (key === "privacyLevel") {
            const mediaType = postType === "TIKTOK_PHOTO" ? "photo" : "video";
            return (
              <TikTokPrivacyKnob
                key={key}
                brandId={brandId}
                value={value}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
                mediaType={mediaType}
              />
            );
          }
          if (key === "subreddit") {
            return (
              <RedditSubredditKnob
                key={key}
                brandId={brandId}
                value={value}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }
          if (key === "flairId") {
            return (
              <RedditFlairKnob
                key={key}
                brandId={brandId}
                value={value}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
                subreddit={(draft.platformKnobs.subreddit as string) ?? ""}
              />
            );
          }
          if (key === "categoryId") {
            return (
              <YoutubeCategoryKnob
                key={key}
                brandId={brandId}
                value={value}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }
          if (key === "playlistId") {
            return (
              <YoutubePlaylistKnob
                key={key}
                brandId={brandId}
                value={value}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }
          if (key === "pageId") {
            return (
              <FacebookPageKnob
                key={key}
                brandId={brandId}
                value={value}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }
          // ── Reach knobs (Instagram catalog audio / trial / partnership,
          //    TikTok Commercial Music). Checked before locationId: IG uses
          //    the same key for a Facebook Page id, GBP for its location.
          const isInstagram = postType.startsWith("IG_");
          if (key === "audioConfiguration") {
            return (
              <InstagramAudioKnob
                key={key}
                brandId={brandId}
                value={draft.platformKnobs.audioConfiguration}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }
          if (key === "trialParams") {
            return (
              <InstagramTrialKnob
                key={key}
                value={draft.platformKnobs.trialParams}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }
          if (key === "isPaidPartnership") {
            const sponsors = draft.platformKnobs.brandedContentSponsors;
            return (
              <InstagramPaidPartnershipKnob
                key={key}
                value={draft.platformKnobs.isPaidPartnership}
                sponsors={Array.isArray(sponsors) ? sponsors : []}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }
          if (key === "brandedContentSponsors") {
            return (
              <InstagramSponsorsKnob
                key={key}
                value={draft.platformKnobs.brandedContentSponsors}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }
          if (key === "commentsEnabled") {
            return (
              <InstagramCommentsKnob
                key={key}
                value={draft.platformKnobs.commentsEnabled}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }
          if (key === "locationId" && isInstagram) {
            return (
              <InstagramLocationKnob
                key={key}
                value={draft.platformKnobs.locationId}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }
          if (key === "musicSoundInfo") {
            return (
              <TikTokMusicKnob
                key={key}
                brandId={brandId}
                value={draft.platformKnobs.musicSoundInfo}
                onChange={(v) => updateKnob(key, v)}
                originalVolume={draft.platformKnobs.videoOriginalSoundVolume}
                onOriginalVolumeChange={(v) => updateKnob("videoOriginalSoundVolume", v)}
                isVideo={postType === "TIKTOK_VIDEO"}
                disabled={disabled}
              />
            );
          }
          if (key === "locationId") {
            return (
              <GmbLocationKnob
                key={key}
                brandId={brandId}
                value={value}
                disabled={disabled}
                onChange={(v) => updateKnob(key, v)}
              />
            );
          }

          if (key === "callToAction") {
            // GBP — type+url object
            const cta = (draft.platformKnobs.callToAction ?? {}) as { type?: string; url?: string };
            return (
              <div key={key} className="space-y-1">
                <label className="text-xs font-medium uppercase text-muted-foreground">
                  {t("editor.callToAction")}
                </label>
                <div className="flex gap-2">
                  <select
                    value={cta.type ?? ""}
                    disabled={disabled}
                    onChange={(e) =>
                      updateKnob("callToAction", { ...cta, type: e.target.value })
                    }
                    className="rounded border px-2 py-1 text-xs"
                  >
                    <option value="">{t("editor.ctaNone")}</option>
                    <option value="LEARN_MORE">{t("editor.ctaLearnMore")}</option>
                    <option value="BOOK">{t("editor.ctaBook")}</option>
                    <option value="ORDER">{t("editor.ctaOrder")}</option>
                    <option value="SHOP">{t("editor.ctaShop")}</option>
                    <option value="SIGN_UP">{t("editor.ctaSignUp")}</option>
                    <option value="CALL">{t("editor.ctaCall")}</option>
                  </select>
                  <input
                    type="url"
                    placeholder="https://…"
                    value={cta.url ?? ""}
                    disabled={disabled}
                    onChange={(e) =>
                      updateKnob("callToAction", { ...cta, url: e.target.value })
                    }
                    className="flex-1 rounded border px-2 py-1 text-xs"
                  />
                </div>
              </div>
            );
          }
          if (
            key === "madeForKids" ||
            key === "contentPreviewConfirmed" ||
            key === "expressConsentGiven" ||
            key === "videoMadeWithAi" ||
            key === "shareToFeed"
          ) {
            return (
              <label key={key} className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={!!value}
                  disabled={disabled}
                  onChange={(e) => updateKnob(key, e.target.checked)}
                />
                <span className="font-medium uppercase text-muted-foreground">{key}</span>
              </label>
            );
          }
          if (key === "visibility") {
            return (
              <div key={key} className="space-y-1">
                <label className="text-xs font-medium uppercase text-muted-foreground">{key}</label>
                <select
                  value={(value as string) || "public"}
                  disabled={disabled}
                  onChange={(e) => updateKnob(key, e.target.value)}
                  className="rounded border px-2 py-1 text-xs"
                >
                  <option value="public">{t("editor.visibility.public")}</option>
                  <option value="unlisted">{t("editor.visibility.unlisted")}</option>
                  <option value="private">{t("editor.visibility.private")}</option>
                </select>
              </div>
            );
          }
          return (
            <div key={key} className="space-y-1">
              <label className="text-xs font-medium uppercase text-muted-foreground">{key}</label>
              <input
                type="text"
                value={value as string}
                disabled={disabled}
                onChange={(e) => updateKnob(key, e.target.value)}
                className="block w-full rounded border px-3 py-2 text-sm"
              />
            </div>
          );
        })}

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={!dirty || saving || disabled}>
          {saving ? t("editor.saving") : t("editor.save")}
        </Button>
      </div>
    </div>
  );

  if (!showPreview) return editorBody;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(280px,360px)] gap-4">
      <div>{editorBody}</div>
      <aside className="lg:sticky lg:top-4 self-start space-y-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {t("editor.preview")}
        </div>
        <PostTypePreview
          postType={postType}
          draft={draft}
          brandHandle={brandHandle}
          brandName={brandName}
        />
      </aside>
    </div>
  );
}
